import { Injectable } from '@angular/core';
import { EventService } from './event.service';
import { Store } from '@ngrx/store';
import {  deleteUndefinedEntriesInPlace, removeFromListByIndex, copyTitle } from '../helpers/helpers';
import { Sponsor, sponsorStatus } from 'globals/store/sponsors.data';
import { ProgramService } from './program.service';
import { SessionInfo, SponsorSessionInfo } from '../store/reselectors/performerSponsors.reselector';
import { Ranking } from 'globals/store/events.data';
import { EntitiesService } from './entities.service';
import { Attendee } from 'globals/store/attendees.data';
import { EntityBase } from 'globals/store/entity.shared.data';
import { AttendeesViewInfo, ShareableItem } from 'globals/store/share.data';
import { LogService } from './log.service';
import { ShareService } from './share.service';
import { createId, jsonCopyObject } from 'globals/helpers/helpers';
import { doc, Firestore, setDoc, updateDoc } from '@angular/fire/firestore';

// separating this out into its own service for better separation of concerns / clarity between services
@Injectable({ providedIn: 'root' })
export class AttendeesService {
  readonly _file = 'attendees.service.ts'

  constructor(
    private log: LogService,
    private programService: ProgramService,
    private eventService: EventService,
    private entitiesService: EntitiesService,
    private shareService: ShareService,
    public store: Store,
    private db: Firestore,
  ) {}
  
  getAttendeeBase = (partialSponsor:Partial<Attendee>):Attendee=>({
    id: createId(),
    social: [],
    labels: [],
    name: '',
    
    ...jsonCopyObject(partialSponsor)
  })

  async duplicateAttendees(attendees: Attendee[]) {
    const newAttendees = attendees.map(attendee => {
      deleteUndefinedEntriesInPlace(attendee)
      const newAttendee = jsonCopyObject(attendee)
      newAttendee.name = copyTitle(newAttendee.name, Object.values(attendees).map(e => e.name))
      const id = createId()
      delete newAttendee.confirmed
      newAttendee.id = id
      return newAttendee
    })
    await this.eventService.saveEventPageEntities('attendees', newAttendees)
  }
  
  async duplicateAttendee(attendee: Attendee) {
    await this.duplicateAttendees([attendee])
  }

  async addAttendee({ name }: {name?: string}): Promise<Attendee> {
    const attendee: Attendee = this.getAttendeeBase({ name })
    await this.eventService.saveEventPageEntities('attendees', [attendee])
    return attendee
  }

  async addMultipleAttendees(partialAttendees:Partial<Attendee>[]): Promise<Attendee[]> {
    const newAttendees = partialAttendees.map(ps=>this.getAttendeeBase(ps))
    await this.eventService.saveEventPageEntities('attendees', newAttendees)
    return newAttendees
  }

  addAttendeeToMultipleSessions(sessions: SessionInfo[], attendeeId: string) {
    this.programService.tagEntityToMultipleSessions(sessions, attendeeId, 'attendeeTags')
  }

  addSponsorsToProgram(session: SessionInfo, sponsorIds: string[]) {
    const { programId, id } = session
    this.programService.getProgramPromise(programId).then(program => {
      sponsorIds.forEach(sponsorId => {
        program.sessions_obj[id].sponsorIds.push(sponsorId)
        program.sessions_obj[id].sponsorStatus[sponsorId] = sponsorStatus.Pending
      })
      this.programService.upsertProgram(program)
    })
  }

  deleteSponsorFromSession(session: SponsorSessionInfo | SessionInfo, sponsorId: string) {
    const { programId, id } = session
    this.programService.getProgramPromise(programId).then(program => {
      const { sponsorStatus, sponsorIds } = program.sessions_obj[id]
      delete sponsorStatus[sponsorId]
      removeFromListByIndex(sponsorIds, sponsorIds.findIndex(i => sponsorId === i))
      this.programService.upsertProgram(program)
    })
  }

  async updateTypes(attendeeTypes: Ranking[]) {
    attendeeTypes.forEach((level, i) => level.rank = attendeeTypes.length - i)
    const event = await this.eventService.getCurrentEvent()
    this.eventService.updateEvent(event.eventId, { attendeeTypes })
  }

  async updateEntities(entities: EntityBase[]) {
    await this.updateAttendees(entities)
  }

  async updateAttendees(updatedAttendees: Attendee[]) {
    console.log({ updatedAttendees })
    updatedAttendees.forEach(deleteUndefinedEntriesInPlace)
    await this.eventService.saveEventPageEntities('attendees', updatedAttendees)
    return updatedAttendees
  }

  updateAttendee(attendee: Attendee) {
    this.updateAttendees([attendee])
  }

  async templateAttendees(attendees: Sponsor[]) {
    const attendeeTemplates = await this.entitiesService.createEntityTemplates('attendee', attendees)
    const updatedAttendees = attendees.map((s, i) => {
      const { dateAdded, dateModified, templateId, versionNumber } = attendeeTemplates[i]
      const updatedSponsor: Sponsor = { ...s, dateAdded, dateModified, templateId, versionNumber }
      return updatedSponsor
    })
    await this.updateAttendees(updatedAttendees)
  }

  async deleteAttendee(attendee: Attendee) {
    await this.deleteAttendees([attendee])
  }

  async deleteAttendees(attendees: Attendee[]) {
    await this.eventService.deleteFromEventPages('attendees', attendees.map(s => s.id))
  }

  async addAttendeeShare(eventId: string) {
    try {
      const publishCode = await this.shareService.createSlugCode()
      const info: AttendeesViewInfo = { eventId, publishCode, status: 'On' }
      const insertObject: ShareableItem = {
        type: 'attendeesView',
        code: publishCode,
        info: info,
        eventId: this.shareService.eventId,
        accountId: this.shareService.accountId
      }
      await setDoc(doc(this.db, '/shareables/' + publishCode), insertObject)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'addNewSharedNotes',
        message:'There was an error updating the sharable',
        error
      })
    }
  }

  async changeShareStatus(info: AttendeesViewInfo) {
    console.log(info)
    try {
      await updateDoc(doc(this.db, '/shareables/' + info.publishCode), { info }).catch(response=>{
        this.log.error({ file:this._file, function:'changeNotesShareStatus', message:'There was an error updating the status', data:{ info }, error: new Error(), response} )
      })
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changeNotesShareStatus',
        message:'There was an error updating the facilitator notes share status',
        error
      })
    }
  }

}